import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Sparkles } from "lucide-react";
import { api, type CarouselItem } from "@/lib/api";
import LlmReceiveProgress from "@/components/LlmReceiveProgress";
import { estimateLlmProgress } from "@/lib/llmProgress";
import { useNowTick } from "@/lib/useNowTick";
import { AiActionButton } from "@/components/studioTabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { ChangeEvent } from "react";

type Props = {
  item: CarouselItem;
  caption: string;
  onCaption: (v: string) => void;
  onGoRender?: () => void;
  autoStart?: boolean;
  abortPending?: boolean;
  onAbort?: () => void;
};

export default function FeedPostPanel({
  item,
  caption,
  onCaption,
  onGoRender,
  autoStart = false,
  abortPending,
  onAbort,
}: Props) {
  const qc = useQueryClient();
  const slide0 = item.copy?.slides?.[0];
  const [heading, setHeading] = useState(String(slide0?.heading || item.hook || ""));
  const [body, setBody] = useState(String(slide0?.body || ""));
  const [feedback, setFeedback] = useState("");
  const [started, setStarted] = useState(false);

  const copyM = useMutation({
    mutationFn: (note: string) => api.feedPostCopy(item.id, note),
    onSuccess: (data) => {
      qc.setQueryData(["carousel", item.id], data);
      const next = data.copy?.slides?.[0];
      setHeading(String(next?.heading || data.hook || ""));
      setBody(String(next?.body || ""));
      onCaption(data.caption || "");
      setFeedback("");
    },
  });

  const generating = copyM.isPending || item.llm_status === "feed_post_copy";
  const failed = Boolean(copyM.isError || item.llm_status === "error");
  const now = useNowTick(generating);
  const live = useMemo(
    () =>
      estimateLlmProgress(
        {
          ...(item.llm_progress || {}),
          started_at: item.llm_progress?.started_at || "",
          phase: item.llm_progress?.phase || (generating ? "waiting" : "done"),
        },
        now,
        item.llm_status,
      ),
    [item.llm_progress, item.llm_status, generating, now],
  );
  const waitSec = live.awaiting_text_sec;
  const limitSec = Number(item.llm_progress?.awaiting_text_limit_sec || 60);
  const statusLine =
    item.llm_progress?.hint ||
    item.llm_detail ||
    (generating
      ? waitSec > 0
        ? `Czekam na pierwszy znak ${waitSec}/${limitSec} s · minęło ${live.elapsed_sec} s`
        : "Model dobiera hak i opis…"
      : copyM.error?.message || "Błąd");

  useEffect(() => {
    if (generating) return;
    setHeading(String(slide0?.heading || item.hook || ""));
    setBody(String(slide0?.body || ""));
  }, [item.id, item.hook, slide0?.heading, slide0?.body, generating]);

  useEffect(() => {
    if (!autoStart || started || generating) return;
    const hasText = Boolean(String(slide0?.heading || "").trim() && String(item.caption || "").trim());
    if (hasText) return;
    setStarted(true);
    copyM.mutate("");
  }, [autoStart, started, generating, slide0?.heading, item.caption, copyM]);

  const patchM = useMutation({
    mutationFn: () =>
      api.carouselPatchCopy(item.id, {
        copy: {
          slides: [
            {
              type: "hook",
              kicker: "",
              heading: heading.trim(),
              body: body.trim(),
              footer: "",
            },
          ],
        },
        caption: caption.trim(),
      }),
    onSuccess: (data) => {
      qc.setQueryData(["carousel", item.id], data);
      onCaption(data.caption || caption);
    },
  });

  const hasCopy = Boolean(heading.trim() && (caption || item.caption || "").trim());

  return (
    <div className="space-y-4">
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-slate-900">
            <Sparkles className="h-4 w-4 text-sky-600" aria-hidden />
            Treść posta 1-grafika {item.short_id ? `· ${item.short_id}` : ""}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(generating || failed) && (
            <section
              className={cn(
                "overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-b from-sky-50/80 via-white to-white shadow-sm",
              )}
              aria-live="polite"
            >
              <div className="border-b border-sky-100/80 px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-sky-700/80">
                      {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                      Krok · treść
                    </p>
                    <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">
                      {generating ? "Piszę tekst na grafikę i caption" : "Nie udało się złożyć treści"}
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">{statusLine}</p>
                  </div>
                  {generating && onAbort ? (
                    <Button type="button" size="sm" variant="outline" disabled={abortPending} onClick={onAbort}>
                      Przerwij
                    </Button>
                  ) : null}
                </div>
                <div className="mt-4">
                  <div className="mb-1 flex justify-between text-xs text-slate-600">
                    <span>{Math.round(live.percent)}%</span>
                    <span>{live.eta_sec > 0 ? `~${live.eta_sec}s` : ""}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-sky-100">
                    <div
                      className="h-full rounded-full bg-sky-500 transition-[width] duration-300"
                      style={{ width: `${Math.max(4, Math.min(100, live.percent))}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className="px-5 py-3">
                <LlmReceiveProgress progress={item.llm_progress} llmStatus={item.llm_status} />
              </div>
              {failed && !generating ? (
                <div className="border-t border-sky-100/80 px-5 py-3">
                  <Button type="button" variant="brand" size="sm" onClick={() => copyM.mutate(feedback.trim())}>
                    Generuj treść ponownie
                  </Button>
                </div>
              ) : null}
            </section>
          )}

          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-700">Tekst na grafice (heading)</label>
            <textarea
              className="w-full rounded-md border border-input bg-background p-2 text-sm leading-relaxed"
              value={heading}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setHeading(e.target.value)}
              rows={2}
              disabled={generating}
              placeholder="Jedno ostre zdanie / cytat / pytanie…"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-700">Dopisek na grafice (opcjonalnie)</label>
            <textarea
              className="w-full rounded-md border border-input bg-background p-2 text-sm leading-relaxed"
              value={body}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setBody(e.target.value)}
              rows={2}
              disabled={generating}
              placeholder="0–2 krótkie linie"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-700">Caption</label>
            <textarea
              className="w-full rounded-md border border-input bg-background p-2 text-sm leading-relaxed"
              value={caption}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onCaption(e.target.value)}
              rows={6}
              disabled={generating}
              placeholder="Opis pod postem…"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-slate-700">Uwaga do modelu (opcjonalnie)</label>
            <textarea
              className="w-full rounded-md border border-input bg-background p-2 text-sm leading-relaxed"
              value={feedback}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFeedback(e.target.value)}
              rows={2}
              disabled={generating}
              placeholder="np. ostrzejszy hak, mniej słów na grafice…"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <AiActionButton
              label={hasCopy ? "Przepisz treść · AI" : "Wygeneruj treść · AI"}
              io="post"
              running={generating}
              failed={failed}
              onClick={() => copyM.mutate(feedback.trim())}
            />
            <Button
              type="button"
              variant="outline"
              disabled={generating || patchM.isPending || !heading.trim()}
              onClick={() => patchM.mutate()}
            >
              Zapisz lokalnie
            </Button>
            {hasCopy && onGoRender ? (
              <Button type="button" variant="brand" disabled={generating} onClick={onGoRender}>
                Idź do Warsztatu (Wyjście)
              </Button>
            ) : null}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Najpierw słowa, potem na Wyjściu: inspiracja → plan składu → PNG z biblioteki elementów.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
